import React, { FC, Fragment, useContext } from 'react';
import { EventWithContext } from '../../../scripts/api';
import { Events, eventType } from '../../../scripts/dataSources/events';
import { All_Seasons } from '../../../scripts/dataSources/seasons';
import { GameID, SeasonID } from '../../../scripts/models/common';
import { BlaseballEvent, ElectionEvent, GameEvent, GameModel, PartialTeam, PlayerModel, SimModel, TeamModel } from '../../../scripts/models/models';
import { Ratings } from '../../../scripts/models/ratings';
import { Settings } from '../../../scripts/settings';
import { StarMath } from '../../../scripts/starMath';
import { capitalize, roundTo, starRound } from '../../../scripts/utility';
import Stars from '../../common/stars/Stars';
import TeamIcon from '../../common/teamIcon/TeamIcon';
import { SettingsContext } from '../../context/settings/SettingsLoader';

import './EventList.scss';

interface gameEventData {
    event: GameEvent,
    game: GameModel,
    day: number,
    seasonId: SeasonID
    affectedPlayers: PlayerModel[],
    context?: EventWithContext,
}

interface electionEventData {
    event: ElectionEvent,
    seasonId: SeasonID,
    affectedPlayers: PlayerModel[],
    context?: EventWithContext
}

interface props {
    data: {

        gameEvents: GameEvent[],
        electionEvents: ElectionEvent[],
        eventsWithTeamStates?: EventWithContext[],
        gamesById: Record<GameID, GameModel>,
        simModel: SimModel,
        allPlayers: PlayerModel[],
        allTeams: TeamModel[]
    },
    excludedEventTypes: eventType[]

}

type eventsByGame = {
    eventsByGame: Record<GameID, {
        events: gameEventData[],
        game: GameModel
    }>
    games: GameID[]
}
type eventsByDay = Record<number, eventsByGame>
type eventsBySeason = Record<SeasonID, eventsByDay>

const EventViewer: FC<props> = (props) => {
    const { data, excludedEventTypes } = props
    const { gameEvents, electionEvents, eventsWithTeamStates: eventsWithContext, gamesById, allPlayers, allTeams } = data

    const allEventText = gameEvents.map(event => event.data.displayText).join(";") + electionEvents.map(event => event.outcomeStrings.join(";")).join(";")

    const players: PlayerModel[] = []
    allPlayers.forEach(player => {
        if (allEventText.includes(player.name)) {
            players.push(player);
        }
    })
    const playerNames = players.map(player => player.name);
    const teamNames = allTeams.map(team => team.name);



    const seenEvents: string[] = [];
    const processedGameEvents: gameEventData[] = []
    gameEvents.forEach(event => {

        // Filtered event types
        if (excludedEventTypes.includes(Events.getEventType(event))) {
            return;
        }

        // Filter out duplicates
        const identifier = (event.game_id as string) + "/" + (event.data.displayOrder) + ";" + (event.data.displayText)
        if (!seenEvents.includes(identifier)) {
            const game = gamesById[event.game_id];

            if (game) {

                const affectedPlayers = players.filter(player => event.data.displayText.includes(player.name))

                let context;
                if (eventsWithContext) {
                    const matches = eventsWithContext.filter(data => (data._type === "GameEvent") && data.event.game_id === event.game_id)
                        .filter(data => data.event.timestamp === event.timestamp);
                    if (matches.length === 1) {
                        context = matches[0]
                    } else {
                        console.warn(`Got ${matches.length} event context matches for event ${event.data.displayText}`)
                    }
                }

                processedGameEvents.push({
                    event,
                    game: game,
                    day: game.day,
                    seasonId: game.seasonId,
                    affectedPlayers,
                    context
                })
            }
        }
        seenEvents.push(identifier)
    })



    processedGameEvents.sort((a, b) => b.day - a.day)
    const eventsBySeason: eventsBySeason = {}
    processedGameEvents.forEach(eventData => {
        if (eventsBySeason[eventData.seasonId] === undefined) {
            eventsBySeason[eventData.seasonId] = {}
        }
        const eventsByDay = eventsBySeason[eventData.seasonId];
        if (eventsByDay[eventData.day] === undefined) {
            eventsByDay[eventData.day] = {
                eventsByGame: {},
                games: []
            };
        }
        const eventsByGameObj = eventsByDay[eventData.day]
        const { eventsByGame, games } = eventsByGameObj
        if (eventsByGame[eventData.game.id] === undefined) {
            eventsByGame[eventData.game.id] = {
                events: [],
                game: eventData.game
            }
            games.push(eventData.game.id)
        }
        eventsByGame[eventData.game.id].events.push(eventData)

    })

    const electionEventsBySeason: Record<SeasonID, electionEventData[]> = {}
    electionEvents.forEach(event => {
        const seasonId = event.seasonId;
        if (electionEventsBySeason[seasonId] === undefined) {
            electionEventsBySeason[seasonId] = []
        }

        const eventText = event.outcomeStrings.join(";")
        const affectedPlayers = players.filter(player => eventText.includes(player.name))

        let context;
        if (eventsWithContext) {
            const matches = eventsWithContext.filter(data => (data._type === "ElectionEvent") && data.event.seasonId === event.seasonId
                && data.event.name === event.name);
            if (matches.length === 1) {
                context = matches[0]
            } else {
                console.warn(`Got ${matches.length} event context matches for event ${event.name}`)
            }
        }
        electionEventsBySeason[seasonId].push({
            event,
            seasonId,
            context,
            affectedPlayers
        })
    })

    // Start with newest season
    const seasons = [...All_Seasons]
    seasons.sort((a, b) => b.num - a.num)

    seasons.forEach(season => {
        electionEventsBySeason[season.id].sort((a, b) => {
            if (a.event.winningTeam === undefined) {
                return -1
            }
            if (b.event.winningTeam === undefined) {
                return 1
            }
            return 0
        })
    })


    return <div className='eventList'>
        {
            seasons.map(season => (
                <Fragment key={season.id}>
                    {electionEventsBySeason[season.id] && (
                        <div className='elections'>
                            <div className="seasonHeader serif">Season {season.num} Elections</div>
                            {electionEventsBySeason[season.id].map((eventData, idx) => (
                                <div className="election card" key={idx}>
                                    <div className="electionHeader header serif">
                                        <span className='optionName'>{eventData.event.name}</span>
                                        {
                                            eventData.event.winningTeam && (
                                                <span className='winner'>
                                                    <Team team={{ ...eventData.event.winningTeam, gameWinner: false }} />
                                                </span>
                                            )
                                        }
                                        <div />
                                    </div>
                                    <div className="outcomes eventContainer">
                                        {eventData.event.outcomeStrings.map((line, idx) => (

                                            <Event key={idx} message={line} playerNames={playerNames} teamNames={teamNames} />
                                        ))}

                                        <EventSummary eventData={eventData} eventWithContext={eventData.context} playerNames={playerNames} />

                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="season">
                        <div className="seasonHeader serif">Season {season.num}</div>
                        <div className="days">
                            {(new Array(120).fill("")).map((_, idx) => {
                                const day = 120 - idx;
                                const eventsByDay = eventsBySeason[season.id]
                                const eventsByGameData = eventsByDay && eventsByDay[day]
                                if (!eventsByGameData) {
                                    // This is normal, many days have no data
                                    return null
                                }
                                return (
                                    <div key={idx} className="day">
                                        <div className="dayHeader serif">Day {day + 1}</div>
                                        <div className="games">

                                            {
                                                eventsByGameData.games.map(gameId => {
                                                    const data = eventsByGameData.eventsByGame[gameId]
                                                    const { events, game } = data
                                                    return (
                                                        <div key={gameId} className='game card'>
                                                            <div className="header">
                                                                <div className='teams serif'>
                                                                    <Team team={game.homeTeam} />
                                                                    <span>vs</span>
                                                                    <Team team={game.awayTeam} />
                                                                </div>
                                                                <div />
                                                            </div>
                                                            {
                                                                events.map((eventData, idx) => (
                                                                    <div className="eventContainer" key={idx}>
                                                                        <Event message={eventData.event.data.displayText} playerNames={playerNames} teamNames={teamNames} />
                                                                        <EventSummary eventData={eventData} eventWithContext={eventData.context} playerNames={playerNames} />
                                                                    </div>
                                                                ))
                                                            }
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </Fragment>
            ))
        }
    </div>
}

export default EventViewer;


interface teamProps {
    team: PartialTeam | TeamModel,
    justIcon?: boolean
}
const Team: FC<teamProps> = (props) => {
    const { team, justIcon = false } = props;
    return (
        <span className={`team ${justIcon ? "icon" : ""}`}>
            <TeamIcon emoji={team.emoji} color={team.primaryColor} shape="Small Circle" />
            <span className='name'>
                {!justIcon && team.name}
            </span>
        </span>
    )
}

interface eventProps {
    message: string,
    playerNames?: string[],
    teamNames?: string[]
}
const Event: FC<eventProps> = (props) => {
    const { message, playerNames, teamNames } = props;
    const segments = Events.splitMessage(message, playerNames, teamNames);

    return (
        <div className="event">
            {
                segments.map((segment, idx) => (
                    <Fragment key={idx}>
                        {segment._type === "plain" ? segment.text
                            : <span className={segment.class}>{segment.text}</span>}
                    </Fragment>
                ))
            }
        </div>
    )

}



function getPlayer(players: PlayerModel[], playerName: string): PlayerModel | false {
    const matches = players.filter(player => player.name === playerName)
    if (matches.length === 1) {
        return matches[0]
    }
    return false;
}

interface summaryProps {
    eventData: gameEventData | electionEventData,
    eventWithContext?: EventWithContext,
    playerNames: string[]

}

const EventSummary: FC<summaryProps> = (props) => {

    const { eventData, eventWithContext, playerNames } = props

    if (!eventWithContext) {
        return null;
    }

    const event = eventData.event;

    const eventText = (Events.isGameEvent(event)) ? event.data.displayText : event.outcomeStrings.join(";");
    const playerNamesInEvent = playerNames.filter(name => eventText.includes(name))
    const eventDebugText = (Events.isGameEvent(event)) ? event.data.displayText : event.name;

    const type = Events.getEventType(eventData.event);
    if (type === "incineration") {
        // There's one player referenced in this event in game
        // Two if it happens in an election
        if (playerNamesInEvent.length === 1 || playerNamesInEvent.length === 2) {
            const playerName = playerNamesInEvent[0]
            const teamMatches = eventWithContext.teamsBefore.filter(team => team.roster.map(player => player.name).includes(playerName))
            if (teamMatches.length === 1) {
                const teamBefore = teamMatches[0];
                const teamAfter = eventWithContext.teamsAfter.filter(team => team.id === teamBefore.id)[0]; // This is safe to assume, right?

                const replacements = teamAfter.roster.filter(player => !teamBefore.roster.map(p => p.id).includes(player.id));
                const replacementName = replacements.length === 1 ? replacements[0].name : "(unknown)"

                if (replacements.length === 1) {

                    const beforeMatches = eventWithContext.playersBefore.filter(player => player.name === playerNamesInEvent[0])
                    const afterMatches = eventWithContext.playersAfter.filter(player => player.name === replacementName)

                    if (beforeMatches.length === 1 && afterMatches.length === 1) {
                        const before = beforeMatches[0]
                        const after = afterMatches[0]
                        return <div className='summary'>
                            <div className="line"><Team team={teamBefore} justIcon /> {playerNamesInEvent[0]} was replaced by {replacementName}</div>
                            <Compare before={before} after={after} event={event} />
                        </div>
                    }

                }

                return <div className='summary'>
                    <div className="line"><Team team={teamBefore} justIcon /> {playerNamesInEvent[0]} was replaced by {replacementName}</div>
                </div>
            }
        }
        console.warn(`Incineration event couldn't be processed [${eventDebugText}]`)
        return null;
    }

    if (type === "burp-miss") {
        return <div className="summary">
            <div className="line subtle">They get buried... For now</div>
        </div>
    }

    if (type === "burp-hit" || type === "shadow-swap" || type === "yeet") {

        const firstPlayerMentioned = eventText.indexOf(playerNamesInEvent[0]) < eventText.indexOf(playerNamesInEvent[1]) ? playerNamesInEvent[0] : playerNamesInEvent[1]
        const secondPlayerMentioned = firstPlayerMentioned === playerNamesInEvent[0] ? playerNamesInEvent[1] : playerNamesInEvent[0]

        const firstIsOldest = (type === "yeet");
        const oldPlayer = (firstIsOldest) ? firstPlayerMentioned : secondPlayerMentioned;
        const newPlayer = (firstIsOldest) ? secondPlayerMentioned : firstPlayerMentioned;

        const teamMatches = eventWithContext.teamsBefore.filter(team => team.roster.map(player => player.name).includes(oldPlayer))
        if (teamMatches.length === 1) {
            const beforeTeam = teamMatches[0];

            if (playerNamesInEvent.length === 2) {


                const beforeMatches = eventWithContext.playersBefore.filter(player => player.name === oldPlayer)
                const afterMatches = eventWithContext.playersAfter.filter(player => player.name === newPlayer)

                if (beforeMatches.length === 1 && afterMatches.length === 1) {
                    const before = beforeMatches[0]
                    const after = afterMatches[0]
                    return <div className='summary'>
                        <div className="line">{newPlayer} joins the <Team team={beforeTeam} /></div>
                        <div className="line">{oldPlayer} enters the shadows...</div>
                        <Compare before={before} after={after} event={event} />
                    </div>
                }


                return <div className="summary">
                    <div className="line">{newPlayer} joins the <Team team={beforeTeam} /></div>
                    <div className="line">{oldPlayer} enters the shadows...</div>
                </div>
            }
        }



        console.warn(`Burp + Hit event couldn't be processed [${eventDebugText}]`)
        return null;
    }

    if (type === "sidequest") {
        if (playerNamesInEvent.length === 1 || playerNamesInEvent.length === 2) {
            const playerName = playerNamesInEvent[0]
            const teamMatches = eventWithContext.teamsBefore.filter(team => team.roster.map(player => player.name).includes(playerName))
            if (teamMatches.length === 1) {
                const teamBefore = teamMatches[0];
                const teamAfter = eventWithContext.teamsAfter.filter(team => team.id === teamBefore.id)[0]; // This is safe to assume, right?

                const activeBefore = teamBefore.roster.filter(player => player.rosterSlots.filter(slot => slot.location !== "SHADOWS").length > 0)
                const activeAfter = teamAfter.roster.filter(player => player.rosterSlots.filter(slot => slot.location !== "SHADOWS").length > 0)



                const replacements = activeAfter.filter(player => !activeBefore.map(p => p.id).includes(player.id));
                if (replacements.length === 1) {
                    const replacementName = replacements[0].name;

                    const beforeMatches = eventWithContext.playersBefore.filter(player => player.name === playerName)
                    const afterMatches = eventWithContext.playersAfter.filter(player => player.name === replacementName)

                    if (beforeMatches.length === 1 && afterMatches.length === 1) {
                        const before = beforeMatches[0]
                        const after = afterMatches[0]
                        return <div className='summary'>
                            <div className="line"><Team team={teamBefore} justIcon /> {playerName} was replaced by {replacementName}</div>
                            <div className="line">{playerName} enters the shadows.</div>
                            <Compare before={before} after={after} event={event} />
                        </div>
                    }

                    return <div className='summary'>
                        <div className="line"><Team team={teamBefore} justIcon /> {playerName} was replaced by {replacementName}</div>
                        <div className="line">{playerName} enters the shadows.</div>
                    </div>
                }

                return <div className='summary'>
                    <div className="line"><Team team={teamBefore} justIcon /> {playerName} enters the shadows with no replacement</div>
                </div>
            }
        }


        console.warn(`Sidequest Embarkation event couldn't be processed [${eventDebugText}]`)
        return null;
    }

    if (type === "alternation" || type === "quest-progress" || type === "favorable-rule" || type === "stat-reroll") {
        // One player has changed stats
        if (playerNamesInEvent.length === 1) {
            const playerName = playerNamesInEvent[0]

            const playerBefore = getPlayer(eventWithContext.playersBefore, playerName)
            const playerAfter = getPlayer(eventWithContext.playersAfter, playerName)
            return <div className="summary">
                <Compare before={playerBefore} after={playerAfter} event={event} />
            </div>
        }

        console.warn(`${type} event couldn't be processed [${eventDebugText}]`)
        return null;

    }

    return null;
}



interface compareProps {
    before: PlayerModel | false,
    after: PlayerModel | false,
    event: BlaseballEvent
}
const Compare: FC<compareProps> = (props) => {
    const { before, after, event } = props


    const settings = useContext(SettingsContext)
    const usePrecise = !!(Settings.lookupSetting("Show precise stars", settings));

    if (!before || !after) {
        console.warn(`Missing match to compare`);
        console.log(event)
        return null;
    }

    const isReplacement = before.id !== after.id; //

    const categoriesToCompare = [
        Ratings.Batting,
        Ratings.Running,
        Ratings.Defense,
        Ratings.Pitching,
        Ratings.Vibes,
    ]

    const beforeName = (isReplacement) ? before.name : "Before"
    const afterName = (isReplacement) ? after.name : "After"

    const oneStat = categoriesToCompare.filter((category) => {
        const valueBefore = starRound(StarMath.getValueForPlayer(category, before), usePrecise);
        const valueAfter = starRound(StarMath.getValueForPlayer(category, after), usePrecise)
        return valueBefore !== valueAfter
    }).length === 1

    return <div className='comparison serif'>

        <table>
            <tbody>

                {
                    isReplacement ? (

                        <tr>
                            <th></th>
                            <th>{beforeName}</th>
                            <th>{afterName}</th>
                        </tr>
                    ) : null
                }
                {
                    categoriesToCompare.map((category, idx) => {
                        const valueBefore = starRound(StarMath.getValueForPlayer(category, before), usePrecise);
                        const valueAfter = starRound(StarMath.getValueForPlayer(category, after), usePrecise)
                        const delta = roundTo(valueAfter - valueBefore, 2);
                        const operator = (delta > 0) ? "+" : "-"
                        let starText = (delta === 1) ? "star" : "stars"
                        if (oneStat) {
                            starText += " overall"
                        }

                        if (isReplacement) {
                            return <tr className='replacement' key={idx}>
                                <td className='category'>{capitalize(category.category)}</td>
                                <td className='stars'><Stars starCount={valueBefore} /></td>
                                <td className='stars'><Stars starCount={valueAfter} /></td>
                                {(delta !== 0) && <td className='delta'>{operator}{Math.abs(delta)} {starText}</td>}
                            </tr>
                        } else {
                            if (delta === 0) {
                                return null
                            }
                            return <tr className='change' key={idx}>
                                <td className='category'>{capitalize(category.category)}</td>
                                <td className='delta'>{operator}{Math.abs(delta)} {starText}</td>
                            </tr>
                        }
                    })
                }
            </tbody>
        </table>
    </div>
}
